/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { OpenInNew } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import AnalyticTable from 'components/kea/tables/AnalyticTable';
import CardContainer from 'components/kea/CardContainer';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import AnalyticsMetricBar from '../components/AnalyticsMetricBar';
import AnalyticsDateRangePicker from '../components/AnalyticsDateRangePicker';
import {
  loadAnalyticsReport,
  selectPopularProducts,
  selectTimeframe,
  actions as analyticsActions,
} from 'slices/AnalyticsSlice';
import styles from './PopularProducts.module.scss';

export function buildProductUrl(storefrontUrl, productId) {
  if (!storefrontUrl || !productId) return null;
  try {
    const base = storefrontUrl.endsWith('/') ? storefrontUrl : `${storefrontUrl}/`;
    const url = new URL('index.php', base);
    url.searchParams.set('id_product', String(productId));
    url.searchParams.set('controller', 'product');
    return url.toString();
  } catch (error) {
    return null;
  }
}

/**
 * Bridge-backed Popular Products from Search page.
 *
 * Shows which products users clicked on from search results.
 * Data is sourced from kea_search_analytics event_type="click",
 * grouped by clicked_product_id, enriched with product name via LEFT JOIN.
 *
 * The product id and translated name stay separate. If the product has since
 * been deleted, the name is shown as unavailable while the recorded id remains visible.
 */
const PopularProductsBridge = () => {
  const dispatch = useDispatch();
  const localizeMessage = useLocalizedMessage();
  const popularProducts = useSelector(selectPopularProducts);
  const timeframe = useSelector(selectTimeframe);
  const { currentShop } = useSelector((store) => store.app);
  const { storefrontUrl } = useShellAppContext();

  const { isFetching, data, error } = popularProducts;
  const maximumClicks = Math.max(1, ...data.map((row) => Number(row.count || 0)));

  useEffect(() => {
    if (!currentShop) return;
    dispatch(loadAnalyticsReport({ shopId: currentShop, reportType: 'products_popular_from_search' }));
  }, [dispatch, currentShop, timeframe]);

  const handleTimeframeChange = (range) => {
    dispatch(analyticsActions.setTimeframe(range));
  };

  const headCells = [
    { id: 'productId', numeric: true, label: localizeMessage('analytics.table.productId') },
    { id: 'name', numeric: false, label: localizeMessage('analytics.table.productName') },
    {
      id: 'count',
      numeric: true,
      label: localizeMessage('analytics.table.clicks'),
      render: (value) => (
        <AnalyticsMetricBar
          value={value}
          maximum={maximumClicks}
          label={localizeMessage('analytics.table.clicks')}
          tone="clickEvents"
        />
      ),
    },
    {
      id: 'productUrl',
      numeric: false,
      label: '',
      sortable: false,
      render: (value, row) => value && (
        <a
          className={styles.productLink}
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${localizeMessage('analytics.table.openProduct')}: ${row.name}`}
        >
          {localizeMessage('analytics.table.open')}
          <OpenInNew fontSize="small" aria-hidden="true" />
        </a>
      ),
    },
  ];

  const prepareRows = (rows) => rows.map((el) => ({
    ...el,
    id: String(el.id),
    productId: el.productId ?? el.id,
    name: el.name || localizeMessage('analytics.table.productUnavailable'),
    productUrl: buildProductUrl(storefrontUrl, el.productId ?? el.id),
  }));

  return (
    <SearchFeaturePageShell
      pageId="analyticPopularProducts"
      actions={(
        <AnalyticsDateRangePicker
          timeframe={timeframe}
          onSubmitRange={handleTimeframeChange}
          isFetching={isFetching}
          localizeMessage={localizeMessage}
        />
      )}
    >
      <Grid spacing={4} container>
        <Grid item xs={12}>
          <CardContainer
            headerId="search.analytics.popularProducts.header"
            styleName="noMargin"
            tableSurface
            isFetching={isFetching}
            error={error}
          >
            <AnalyticTable
              rows={prepareRows(data)}
              headCells={headCells}
              orderById="count"
            />
          </CardContainer>
        </Grid>
      </Grid>
    </SearchFeaturePageShell>
  );
};

export default PopularProductsBridge;
